import { CellFormatConfig, formatCell, ReportCellType } from './cell-format';

/**
 * Config for rendering a report cell as HTML for the emailed report. Extends the
 * standard formatting config with the Staff Site base URL so on-screen
 * routerLinks can be reproduced as absolute links in the email.
 */
export interface ReportRenderConfig extends CellFormatConfig {
  /** Absolute Staff Site base, e.g. "https://app.hellocapitan.com" (no trailing slash). */
  baseUrl: string;
  /** Whether the org has multiple locations — controls location sub-lines (e.g. roster). */
  isMultiLocation?: boolean;
  /**
   * Gift-card balances by gift id, supplied by the emailed-reports service (the
   * Staff Site fetches these from the POS at runtime; they are not on the report
   * model). When absent, the gifts report renders the same "Loading balance..."
   * placeholder the table shows before its lookup resolves.
   */
  giftCardBalances?: { [giftId: number]: { state: string; balance?: string } };
}

const HTML_ESCAPES: { [c: string]: string } = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;',
};

/** Escape a value for use in HTML text or a double-quoted attribute. */
export function escapeHtml(value: string | number | null | undefined): string {
  if (value === null || value === undefined) { return ''; }
  return String(value).replace(/[&<>"']/g, c => HTML_ESCAPES[c]);
}

/** Text wrapped in a span carrying a CSS class (mirrors the on-screen text-* spans). */
export function coloredText(cssClass: string, text: string): string {
  return `<span class="${cssClass}">${escapeHtml(text)}</span>`;
}

/**
 * An absolute Staff Site link, reproducing a routerLink. `path` begins with '/'
 * (e.g. "/customers/42/payments"). The on-screen tables use SPA routerLinks;
 * the email needs absolute hrefs.
 */
export function link(config: ReportRenderConfig, path: string, innerHtml: string,
                     opts: { query?: { [k: string]: string | number }, target?: string, cls?: string } = {}): string {
  let href = config.baseUrl + path;
  if (opts.query) {
    const qs = Object.keys(opts.query)
      .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(String(opts.query[k]))}`).join('&');
    href += '?' + qs;
  }
  const target = opts.target ? ` target="${escapeHtml(opts.target)}"` : '';
  const cls = opts.cls ? ` class="${escapeHtml(opts.cls)}"` : '';
  return `<a href="${escapeHtml(href)}"${cls}${target}>${innerHtml}</a>`;
}

/** Join cell lines with <br>, dropping empty parts (mirrors multi-line cells). */
export function lines(...parts: (string | null | undefined)[]): string {
  return parts.filter(p => p !== null && p !== undefined && p !== '').join('<br>');
}

/**
 * Render a standard (non-custom) cell as HTML, mirroring app-generic-table-cell:
 * "#id" / "{symbol}{amount}" / a no-wrap date, the muted "None" placeholder when
 * absent, and plain (blank-when-empty) text otherwise.
 */
export function renderStandardCell(rawValue: string | null | undefined,
                                   type: ReportCellType,
                                   config: ReportRenderConfig): string {
  const formatted = formatCell(rawValue, type, config);
  if (formatted === null) {
    return type === ReportCellType.text ? '' : coloredText('text-muted', 'None');
  }
  if (type === ReportCellType.date) {
    return `<span class="text-nowrap">${escapeHtml(formatted)}</span>`;
  }
  return escapeHtml(formatted);
}
